import React from "react";
import { Alert, Checkbox, Text, Anchor, Box, Loader, Center, NumberInput, Group, Divider } from "@mantine/core";
import { IconAlertCircle, IconCircleCheck, IconAlertTriangle } from "@tabler/icons-react";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { withTranslation, WithTranslation } from "react-i18next";
import classes from "./SubscriptionCheckout.module.css";

const PAYPAL_PLAN_ID = "P-06316034F00035026NHFL57Y";
const PAYPAL_CLIENT_ID = "AeUBpsIJtNCqe-CU5OBOH3PRZ6fiFqr-P0q_vYdh2wlX3fGq1j2NbNc6qMDQ-LmM2diIzXQg45cu0rqs";
const PRICE_PER_UNIT = 19.99;

type CheckoutStatus = "idle" | "success" | "error" | "cancelled";

interface SubscriptionCheckoutProviderState {
  tosAccepted: boolean;
  status: CheckoutStatus;
  subscriptionId: string | null;
  errorMessage: string | null;
  quantity: number;
}

interface SubscriptionCheckoutProviderProps extends WithTranslation {}

class SubscriptionCheckoutProvider extends React.Component<
  SubscriptionCheckoutProviderProps,
  SubscriptionCheckoutProviderState
> {
  state: SubscriptionCheckoutProviderState = {
    tosAccepted: false,
    status: "idle",
    subscriptionId: null,
    errorMessage: null,
    quantity: 1,
  };

  onApprove = async (data: { subscriptionID?: string | null }): Promise<void> => {
    this.setState({
      status: "success",
      subscriptionId: data.subscriptionID ?? null,
    });
  };

  onError = (err: Record<string, unknown>) => {
    console.error("PayPal subscription error:", err);
    this.setState({
      status: "error",
      errorMessage: this.props.t("subscription.checkout.errorMessage"),
    });
  };

  onCancel = () => {
    this.setState({ status: "cancelled" });
  };

  render() {
    const { t } = this.props;
    const { tosAccepted, status, subscriptionId, errorMessage, quantity } = this.state;
    const total = (PRICE_PER_UNIT * quantity).toFixed(2).replace(".", ",");

    if (status === "success") {
      return (
        <Alert
          icon={<IconCircleCheck size={20} />}
          title={t("subscription.checkout.successTitle")}
          color="green"
          radius="md"
        >
          <Text size="sm">{t("subscription.checkout.successMessage")}</Text>
          {subscriptionId && (
            <Text size="xs" c="dimmed" mt="xs">
              {t("subscription.checkout.subscriptionId")}: {subscriptionId}
            </Text>
          )}
        </Alert>
      );
    }

    return (
      <Box>
        {status === "error" && (
          <Alert
            icon={<IconAlertCircle size={20} />}
            title={t("subscription.checkout.errorTitle")}
            color="red"
            radius="md"
            mb="md"
            withCloseButton
            onClose={() => this.setState({ status: "idle", errorMessage: null })}
          >
            <Text size="sm">{errorMessage}</Text>
          </Alert>
        )}

        {status === "cancelled" && (
          <Alert
            icon={<IconAlertTriangle size={20} />}
            title={t("subscription.checkout.cancelledTitle")}
            color="yellow"
            radius="md"
            mb="md"
            withCloseButton
            onClose={() => this.setState({ status: "idle" })}
          >
            <Text size="sm">{t("subscription.checkout.cancelledMessage")}</Text>
          </Alert>
        )}

        {/* Quantity selector */}
        <NumberInput
          label={t("subscription.checkout.quantity.label")}
          description={t("subscription.checkout.quantity.description")}
          min={1}
          max={20}
          value={quantity}
          onChange={(val) => this.setState({ quantity: typeof val === "number" ? val : 1 })}
          mb="sm"
          styles={{ label: { color: "#fff" }, description: { color: "rgba(255,255,255,0.5)" } }}
        />

        <Group justify="space-between" mb="sm">
          <Text size="sm" c="dimmed">{t("subscription.checkout.quantity.total")}</Text>
          <Text size="sm" fw={700} style={{ color: "#fff" }}>
            {total}€ / {t("subscription.checkout.quantity.month")}
          </Text>
        </Group>

        <Divider mb="sm" />

        <Checkbox
          mb="sm"
          checked={tosAccepted}
          onChange={() => this.setState({ tosAccepted: !tosAccepted })}
          label={
            <Text size="xs">
              {t("subscription.checkout.tosCheckbox.prefix")}{" "}
              <Anchor href="/tos" target="_blank" size="xs">
                {t("subscription.checkout.tosCheckbox.tos")}
              </Anchor>
              {" "}{t("subscription.checkout.tosCheckbox.and")}{" "}
              <Anchor href="/privacy_web" target="_blank" size="xs">
                {t("subscription.checkout.tosCheckbox.privacy")}
              </Anchor>
            </Text>
          }
        />

        <div style={{ position: "relative" }} className={classes.checkoutContainer}>
          <PayPalButtons
            style={{ layout: "vertical", color: "gold", shape: "rect", label: "subscribe" }}
            createSubscription={(_data, actions) =>
              actions.subscription.create({ plan_id: PAYPAL_PLAN_ID, quantity: String(quantity) })
            }
            onApprove={this.onApprove}
            onError={this.onError}
            onCancel={this.onCancel}
          />
          {!tosAccepted && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backdropFilter: "blur(3px)",
                backgroundColor: "rgba(0,0,0,0.15)",
                borderRadius: "4px",
                cursor: "not-allowed",
                zIndex: 9999,
              }}
            />
          )}
        </div>
      </Box>
    );
  }
}

const SubscriptionCheckoutWithI18n = withTranslation()(SubscriptionCheckoutProvider);

function PayPalLoadingWrapper() {
  const [{ isPending }] = usePayPalScriptReducer();
  return isPending ? (
    <Center py="xl">
      <Loader color="gray" />
    </Center>
  ) : (
    <SubscriptionCheckoutWithI18n />
  );
}

export function SubscriptionCheckout() {
  return (
    <PayPalScriptProvider
      options={{
        clientId: PAYPAL_CLIENT_ID,
        vault: true,
        intent: "subscription",
      }}
    >
      <PayPalLoadingWrapper />
    </PayPalScriptProvider>
  );
}
